if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    class ProductCard extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onSwatchOver = this.onSwatchOver.bind(this);
        this.onSwatchesLeave = this.onSwatchesLeave.bind(this);
      }

      connectedCallback() {
        this.toggleButton = this.querySelector('[data-quick-toggle]');
        this.errorMessage = this.querySelector('.ob-card__quick-error');
        this.addEventListener('click', this.onClick);
        this.addEventListener('keyup', this.onKeyUp);

        // Colour swatches (snippets/card-colour-swatches.liquid): hover previews a colour, click selects it.
        this.swatches = this.querySelector('.ob-card__swatches--interactive');
        this.image = this.querySelector('.ob-card__image:not(.ob-card__image--alt)');
        if (this.swatches && this.image) {
          this.initialSwatch = this.swatches.querySelector('[data-card-colour][aria-pressed="true"]');
          this.selectedSwatch = this.initialSwatch;
          this.initialImage = { src: this.image.getAttribute('src'), srcset: this.image.getAttribute('srcset') };
          this.swatches.addEventListener('pointerover', this.onSwatchOver);
          this.swatches.addEventListener('pointerleave', this.onSwatchesLeave);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('click', this.onDocumentClick);
        this.swatches?.removeEventListener('pointerover', this.onSwatchOver);
        this.swatches?.removeEventListener('pointerleave', this.onSwatchesLeave);
      }

      onClick(event) {
        if (event.target.closest('[data-quick-toggle]')) {
          this.setQuickOpen(!this.classList.contains('is-quick-open'));
          return;
        }

        const swatch = event.target.closest('[data-card-colour]');
        if (swatch) {
          this.selectColour(swatch);
          return;
        }

        const button = event.target.closest('[data-quick-add-variant]');
        if (button) this.addToCart(button);
      }

      onSwatchOver(event) {
        if (event.pointerType !== 'mouse') return;
        const swatch = event.target.closest('[data-card-colour]');
        if (swatch) this.showColourImage(swatch);
      }

      onSwatchesLeave(event) {
        if (event.pointerType !== 'mouse') return;
        this.showColourImage(this.selectedSwatch);
      }

      selectColour(swatch) {
        this.selectedSwatch = swatch;
        this.swatches.querySelectorAll('[data-card-colour]').forEach((item) => {
          const selected = item === swatch;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        this.showColourImage(swatch);

        // Product links open the chosen colour.
        this.querySelectorAll('.ob-card__image-link, .ob-card__title').forEach((link) => {
          link.href = swatch.dataset.url;
        });

        // Quick-add sizes add the chosen colour: "variantId:available" per size button, in order.
        const sizes = (swatch.dataset.sizes || '').split(',').filter(Boolean);
        const buttons = this.querySelectorAll('.ob-card__sizes .ob-card__size');
        if (sizes.length !== buttons.length) return;
        buttons.forEach((button, index) => {
          const [id, available] = sizes[index].split(':');
          if (id !== '0' && available === '1') {
            button.dataset.quickAddVariant = id;
            button.disabled = false;
          } else {
            delete button.dataset.quickAddVariant;
            button.disabled = true;
          }
        });
      }

      // Swaps the card photo once the new one is decoded, so it never flashes blank.
      showColourImage(swatch) {
        const next =
          !swatch || swatch === this.initialSwatch
            ? this.initialImage
            : { src: swatch.dataset.src, srcset: swatch.dataset.srcset };
        if (!next.src) return;
        // Compare files, not URLs: the same photo can come at different widths.
        const file = (url) => (url || '').split('?')[0];
        this.classList.toggle('is-colour-switched', file(next.src) !== file(this.initialImage.src));
        if (file(this.image.getAttribute('src')) === file(next.src)) {
          this.imageToken = null;
          return;
        }

        const token = (this.imageToken = Symbol());
        const preload = new Image();
        preload.sizes = this.image.sizes;
        if (next.srcset) preload.srcset = next.srcset;
        preload.src = next.src;
        preload
          .decode()
          .catch(() => {})
          .then(() => {
            if (this.imageToken !== token) return;
            if (next.srcset) this.image.srcset = next.srcset;
            else this.image.removeAttribute('srcset');
            this.image.src = next.src;
          });
      }

      onKeyUp(event) {
        if (event.code !== 'Escape' || !this.classList.contains('is-quick-open')) return;
        this.setQuickOpen(false);
        this.toggleButton?.focus();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.setQuickOpen(false);
      }

      setQuickOpen(open) {
        this.classList.toggle('is-quick-open', open);
        this.toggleButton?.setAttribute('aria-expanded', String(open));
        if (open) {
          document.addEventListener('click', this.onDocumentClick);
          this.querySelector('[data-quick-add-variant]')?.focus();
        } else {
          document.removeEventListener('click', this.onDocumentClick);
        }
      }

      showError(message) {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = message || '';
        this.errorMessage.hidden = !message;
      }

      addToCart(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const variantId = button.dataset.quickAddVariant;
        this.showError();
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(button);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-card',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.showError(response.description || response.message);
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-card',
              productVariantId: variantId,
              cartData: response,
            });
            this.setQuickOpen(false);
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings?.error);
          })
          .finally(() => {
            button.classList.remove('is-loading');
            button.removeAttribute('aria-disabled');
          });
      }
    }
  );
}
